import _cloneDeep from 'lodash/cloneDeep';
import {
  GetParentObject,
  ParentThreads,
  QuestionData,
  QuestionDataById,
  QuestionThreadParent,
  QuestionThreadParentBase,
} from './types';

type GetRequiredQuestions = {
  omitQuestionId: number;
  parentQuestions: QuestionThreadParentBase[];
  getParentObject: GetParentObject;
};

const getRequiredQuestions = ({ omitQuestionId, parentQuestions, getParentObject }: GetRequiredQuestions) => {
  const requiredQuestions = _cloneDeep(parentQuestions).filter((parent) => parent.parentId !== omitQuestionId);

  return requiredQuestions.map((parentQuestion) =>
    getParentObject(
      parentQuestion.parentId,
      parentQuestion.parentTypeId,
      parentQuestion.comparison,
      parentQuestion.triggerValue
    )
  );
};

type GetParentQuestionThreads = {
  questionsById: QuestionDataById;
  getParentObject: GetParentObject;
  startingItem?: QuestionData;
  reqQuestionObject?: QuestionThreadParentBase | null;
};

export const getParentQuestionThreads = ({
  startingItem,
  questionsById,
  getParentObject,
  reqQuestionObject,
}: GetParentQuestionThreads): ParentThreads => {
  const parentThreads: ParentThreads = [];

  // create initial threads for immediate parents
  const startingParents = startingItem?.parentQuestions || [];

  startingParents?.forEach((immediateParent: QuestionThreadParent) => {
    const omitQuestionId = reqQuestionObject ? 0 : immediateParent.parentId;
    const requiredQuestions = startingItem?.requireAllParents
      ? getRequiredQuestions({ omitQuestionId, parentQuestions: startingParents, getParentObject })
      : [];

    const thread: QuestionThreadParentBase[] = [
      getParentObject(
        immediateParent.parentId,
        immediateParent.parentTypeId,
        immediateParent.comparison,
        immediateParent.triggerValue,
        requiredQuestions
      ),
    ];

    if (reqQuestionObject) {
      thread.push(reqQuestionObject);
    }

    parentThreads.unshift(thread);
  });

  if (startingParents.length === 0 && reqQuestionObject) {
    // if no parents, but a reqQuestionObject is provided, create a thread with just that object
    parentThreads.unshift([reqQuestionObject]);
  }

  // build each thread in the parentThreads array

  let threadIndex = 0;

  while (threadIndex < parentThreads.length) {
    const parentThread = parentThreads[threadIndex];

    if (!parentThread) {
      break;
    }

    const firstItemInThread = parentThread?.[0];
    const firstQuestionInThread = questionsById[firstItemInThread.parentId];
    let currentQuestion = firstQuestionInThread;

    // loop through the parent chain
    while (currentQuestion) {
      const currentQuestionParents = currentQuestion.parentQuestions;
      let parentObject: QuestionThreadParent | null = {
        parentId: 0,
        parentName: '',
        parentTypeId: 0,
        comparison: '=',
        triggerValue: '',
      };

      // loop through each parent of the current question
      currentQuestionParents?.forEach((parentQuestion, index) => {
        if (index === 0) {
          // if the first parent, continue building the current thread

          parentObject = parentQuestion;
        } else {
          // if a subsequent parent, create a new thread and generate requiredQuestions if needed

          const newThread = _cloneDeep(parentThread);
          const requiredQuestions = currentQuestion?.requireAllParents
            ? getRequiredQuestions({
                omitQuestionId: 0,
                parentQuestions: currentQuestionParents,
                getParentObject,
              })
            : [];
          const newParentItem = getParentObject(
            parentQuestion.parentId,
            parentQuestion.parentTypeId,
            parentQuestion.comparison,
            parentQuestion.triggerValue,
            requiredQuestions
          );

          newThread.unshift(newParentItem);
          parentThreads.push(newThread);
        }
      });

      if (parentObject.parentId > 0) {
        const parentItem = getParentObject(
          parentObject.parentId,
          parentObject.parentTypeId,
          parentObject.comparison,
          parentObject.triggerValue
        );

        parentThread.unshift(parentItem);
        currentQuestion = questionsById[parentObject?.parentId];
      } else {
        break;
      }
    }

    // safety break to prevent infinite loops
    if (threadIndex > 100) {
      break;
    }

    threadIndex++;
  }

  return parentThreads;
};
